import type { AddSegmentRequestData, UpdateSegmentsRequestData } from '@/shared/api/project/types';

import { TDst, TServerSegment, TSrc, TUpdatingSegmentData } from '@/shared/types/Editor';

export type TSegmentMutationText = Omit<TDst, 'audio'> | TSrc;

/**
 * Transcript GET responses can contain short-lived, presigned audio URLs. They are response-only
 * bearer credentials and must never be persisted back through segment mutation endpoints.
 */
export const serializeSegmentMutationText = <T extends TDst | TSrc>(text: T): Omit<T, 'audio'> => {
  const { audio: _audio, ...mutationText } = text as { audio?: string } & T;

  return {
    ...mutationText,
    words: mutationText.words?.map((word) => ({ ...word })),
  } as Omit<T, 'audio'>;
};

type TAddSegmentSource = Pick<
  TServerSegment,
  | 'dst'
  | 'end'
  | 'needs_attention'
  | 'selected_translation_variant'
  | 'speaker'
  | 'src'
  | 'start'
  | 'translation_variants'
>;

export const serializeAddSegmentRequest = (segment: TAddSegmentSource): AddSegmentRequestData => ({
  dst: serializeSegmentMutationText(segment.dst),
  end: segment.end,
  needs_attention: segment.needs_attention,
  selected_translation_variant: segment.selected_translation_variant
    ? { ...segment.selected_translation_variant }
    : segment.selected_translation_variant,
  speaker: segment.speaker,
  src: serializeSegmentMutationText(segment.src),
  start: segment.start,
  translation_variants: segment.translation_variants?.map((variant) => ({ ...variant })),
});

export const serializeUpdateSegmentRequest = (segment: TUpdatingSegmentData): UpdateSegmentsRequestData => ({
  ...segment,
  dst: segment.dst ? serializeSegmentMutationText(segment.dst) : undefined,
  selected_translation_variant: segment.selected_translation_variant
    ? { ...segment.selected_translation_variant }
    : segment.selected_translation_variant,
  src: segment.src ? serializeSegmentMutationText(segment.src) : undefined,
  translation_variants: segment.translation_variants?.map((variant) => ({ ...variant })) ?? segment.translation_variants,
});
